import React from "react";
import {
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

const formatTimestamp = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const formatDuration = (seconds) => {
  if (!Number.isFinite(Number(seconds)) || Number(seconds) < 1) return "0s";

  const totalSeconds = Number(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const remainder = totalSeconds % 60;

  if (!minutes) return `${remainder}s`;
  return `${minutes}m ${remainder}s`;
};

export default function VoiceCallTimeline({ voiceCalls = [] }) {
  if (!voiceCalls.length) {
    return null;
  }

  return (
    <Box sx={{ px: 2, pb: 2.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.4 }}>
        <CallOutlinedIcon sx={{ color: "#0E6346" }} />
        <Typography variant="subtitle2" fontWeight={900}>
          Call Activity
        </Typography>
        <Chip
          size="small"
          label={`${voiceCalls.length} log${voiceCalls.length === 1 ? "" : "s"}`}
          sx={{ fontWeight: 800 }}
        />
      </Stack>

      <Stack spacing={1.2}>
        {voiceCalls.map((call) => (
          <Paper
            key={call.id}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid #D8E7DD",
              overflow: "hidden",
              boxShadow: "0 14px 30px -28px rgba(14,99,70,0.55)",
            }}
          >
            <Box sx={{ px: 1.8, py: 1.4, bgcolor: "#F6FFF7" }}>
              <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                    <Typography variant="subtitle2" fontWeight={900}>
                      {call.direction === "inbound" ? "Inbound call" : "Outbound call"}
                    </Typography>
                    <Chip
                      size="small"
                      label={String(call.status || "logged").replace(/_/g, " ")}
                      sx={{ textTransform: "capitalize", fontWeight: 800 }}
                    />
                    {call.disposition ? (
                      <Chip size="small" variant="outlined" label={String(call.disposition).replace(/_/g, " ")} />
                    ) : null}
                    {call.action_required ? (
                      <Chip size="small" color="warning" label="Action required" sx={{ fontWeight: 800 }} />
                    ) : null}
                  </Stack>

                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    {[call.from_number ? `From ${call.from_number}` : null, call.to_number ? `To ${call.to_number}` : null]
                      .filter(Boolean)
                      .join(" • ") || "Manual call log"}
                  </Typography>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {formatTimestamp(call.occurred_at)}
                </Typography>
              </Stack>
            </Box>

            <Divider />

            <Box sx={{ px: 1.8, py: 1.5, bgcolor: "#FFFFFF" }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1} useFlexGap flexWrap="wrap">
                <Chip
                  size="small"
                  icon={<FiberManualRecordIcon sx={{ fontSize: 10 }} />}
                  label={`Duration: ${formatDuration(call.duration_seconds)}`}
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={call.recording_enabled ? "Recording enabled" : "Recording off"}
                  variant="outlined"
                />
                {call.assigned_to?.name ? (
                  <Chip size="small" label={`Owner: ${call.assigned_to.name}`} variant="outlined" />
                ) : null}
                {call.creator?.name ? (
                  <Chip size="small" label={`Logged by ${call.creator.name}`} variant="outlined" />
                ) : null}
              </Stack>

              {call.summary_text ? (
                <Typography variant="body2" sx={{ mt: 1.3, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {call.summary_text}
                </Typography>
              ) : null}

              {call.wrap_up_note ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: call.summary_text ? 1 : 1.3, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  {call.wrap_up_note}
                </Typography>
              ) : null}
            </Box>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}
